import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Check, CheckCircle2, ArrowRight, ArrowLeft, Bookmark, Sparkles, Lock, Users, Info, Target,
  GraduationCap, Repeat, RotateCcw, Briefcase, Shield, KeyRound, Trophy, HeartHandshake, Star, Minus, Zap,
  Compass, DollarSign, Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCRIPT = { fontFamily: "'Caveat', cursive" };
const ICONS = { GraduationCap, Repeat, RotateCcw, Users, Briefcase, Shield, KeyRound, Trophy, HeartHandshake, Star, Minus };
const STEPS = ['Identity', 'Situation', 'Goals', 'Complete'];

// Shared "glow panel" treatment so the question card reads like the hero (not flat).
const GLOW_PANEL = {
  background: 'linear-gradient(135deg, rgba(10,30,70,0.9), rgba(11,19,48,0.92))',
  border: '1px solid rgba(57,227,220,0.30)',
  boxShadow: '0 0 70px -26px rgba(57,227,220,0.7), inset 0 1px 0 rgba(255,255,255,0.06)',
};

export default function MySituationPage() {
  const { getAuthHeader, user } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [context, setContext] = useState(null);
  const [textVal, setTextVal] = useState('');
  const [phase, setPhase] = useState('intro');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/situation/questions`, getAuthHeader()).catch(() => ({ data: {} })),
      axios.get(`${API}/situation/context`, getAuthHeader()).catch(() => ({ data: {} })),
    ]).then(([q, c]) => {
      setQuestions(q.data.questions || []);
      setAnswers(q.data.answers || {});
      setContext(c.data.context || null);
    }).finally(() => setLoading(false));
  }, [getAuthHeader]);

  const total = questions.length;
  const q = questions[idx];
  const answeredCount = useMemo(() => Object.keys(answers).filter((k) => {
    const v = answers[k]; return Array.isArray(v) ? v.length : !!v;
  }).length, [answers]);
  const pct = total ? Math.round(((done ? total : idx) / total) * 100) : 0;
  const instShort = ((context?.institution || user?.organization_name || '').match(/[A-Za-z][A-Za-z-]+/g) || ['your campus']).slice(-1)[0];

  const persist = (next, completed) => {
    axios.post(`${API}/situation`, { answers: next, ...(completed != null ? { completed } : {}) }, getAuthHeader()).catch(() => {});
  };
  const setSingle = (val) => { const next = { ...answers, [q.id]: val }; setAnswers(next); persist({ [q.id]: val }); };
  const toggleMulti = (val) => {
    const cur = new Set(answers[q.id] || []);
    if (val === 'none') { const next = { ...answers, [q.id]: cur.has('none') ? [] : ['none'] }; setAnswers(next); persist({ [q.id]: next[q.id] }); return; }
    cur.delete('none');
    if (cur.has(val)) cur.delete(val); else cur.add(val);
    const arr = [...cur]; const next = { ...answers, [q.id]: arr }; setAnswers(next); persist({ [q.id]: arr });
  };
  const finish = async () => {
    persist({}, true);
    try { const r = await axios.get(`${API}/situation/context`, getAuthHeader()); setContext(r.data.context); } catch { /* ignore */ }
    setDone(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const next = () => {
    if (q?.allow_text && textVal.trim()) { const arr = [...(answers[q.id] || []), textVal.trim()]; setAnswers({ ...answers, [q.id]: arr }); persist({ [q.id]: arr }); setTextVal(''); }
    if (idx < total - 1) setIdx(idx + 1); else finish();
  };
  const back = () => idx > 0 && setIdx(idx - 1);
  const agree = () => { persist({}, true); toast.success('Applied, FuturePath is now personalized to your journey'); navigate('/app/college-dashboard'); };

  if (loading) return <AppLayout><div className="py-24 flex justify-center"><LoadingSpinner size="lg" /></div></AppLayout>;

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto pb-16" data-testid="my-situation-page">

        {/* ═══ HERO ═══ */}
        <div className="relative overflow-hidden rounded-[22px] mb-5"
          style={{ background: 'linear-gradient(135deg, #071a3a 0%, #0a1e46 55%, #0b1330 100%)', border: '1px solid rgba(57,227,220,0.32)', boxShadow: '0 0 80px -30px rgba(57,227,220,0.75), inset 0 1px 0 rgba(255,255,255,0.05)' }}>
          <motion.div aria-hidden animate={{ opacity: [0.28, 0.5, 0.28] }} transition={{ duration: 7, repeat: Infinity }}
            className="pointer-events-none absolute -right-24 -top-28 h-72 w-72 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle,#39E3DC,transparent 62%)' }} />
          <motion.div aria-hidden animate={{ opacity: [0.22, 0.42, 0.22] }} transition={{ duration: 9, repeat: Infinity }}
            className="pointer-events-none absolute -left-24 -bottom-28 h-72 w-72 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle,#782FE1,transparent 62%)' }} />
          <div className="relative p-6 sm:p-8">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="min-w-0 max-w-xl">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#39E3DC] font-bold">College Experience</p>
                <h1 className="font-outfit text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-1" style={{ textShadow: '0 0 48px rgba(57,227,220,0.35)' }}>My Situation</h1>
                <p className="font-outfit text-xl text-[#a78bfa] font-bold mt-0.5">Let's understand your journey.</p>
                <p className="text-sm text-slate-300/90 mt-3 max-w-lg">Your background, circumstances and current reality help FuturePath create a strategy that actually works for your life, at {instShort} and beyond.</p>
              </div>
              <p className="text-3xl leading-tight text-[#39E3DC] text-right hidden lg:block" style={{ ...SCRIPT, textShadow: '0 0 30px rgba(57,227,220,0.45)' }}>More Than a Student.<br />A Whole You.</p>
            </div>
            <div className="flex items-center gap-2 mt-6">
              {STEPS.map((s, i) => {
                const state = done ? (i < 3 ? 'done' : 'active') : i < 1 ? 'done' : i === 1 ? 'active' : 'todo';
                return (
                  <div key={s} className="flex items-center gap-2">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${state === 'done' ? 'bg-cyan-500/20 border-cyan-400/60 text-cyan-200 shadow-[0_0_16px_-4px_rgba(34,211,238,0.8)]' : state === 'active' ? 'bg-purple-500/25 border-purple-400/70 text-white shadow-[0_0_18px_-3px_rgba(139,92,255,0.9)]' : 'border-white/20 text-slate-300'}`}>
                      {state === 'done' ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    <span className={`text-xs hidden sm:block ${state === 'todo' ? 'text-slate-300' : 'text-slate-200'}`}>{s}</span>
                    {i < STEPS.length - 1 && <span className="w-6 h-px bg-white/15 hidden sm:block" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ═══ INTRO ═══ */}
        {!done && phase === 'intro' && (
          <div className="space-y-5" data-testid="situation-intro">
            <div className="relative overflow-hidden rounded-[20px] p-6 sm:p-8" style={GLOW_PANEL}>
              <div aria-hidden className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full blur-3xl opacity-[0.18]" style={{ background: 'radial-gradient(circle,#39E3DC,transparent 62%)' }} />
              <div className="relative">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#39E3DC] font-bold">Before we begin</p>
                <h2 className="font-outfit text-2xl sm:text-3xl font-extrabold text-white mt-1 max-w-3xl">This isn't a form. It's how FuturePath gets to know the real you.</h2>
                <p className="text-sm sm:text-[15px] text-slate-300/90 mt-2 max-w-3xl">Your major tells part of the story. Your <span className="text-white font-semibold">time, money, responsibilities and confidence</span> tell us whether a plan will actually work. Share a little, and every recommendation, plan and next step adapts to your real life.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
                  {[['~2 min', 'About how long this takes'], ['10', 'Quick questions'], ['100%', 'Private to you'], ['Every page', 'Adapts to your answers']].map(([n, l]) => (
                    <div key={l} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                      <p className="font-outfit text-2xl font-extrabold text-[#39E3DC]" style={{ textShadow: '0 0 20px rgba(57,227,220,0.4)' }}>{n}</p>
                      <p className="text-[11px] text-slate-300 leading-tight mt-0.5">{l}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-outfit text-lg font-bold text-white mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#a78bfa]" /> What your answers actually change</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[[DollarSign, 'Money', 'We keep costs honest and surface scholarships, aid and paid opportunities.', '#34d399'],
                  [Clock, 'Time', "We right-size your plan to the hours your week actually has.", '#39E3DC'],
                  [Compass, 'Direction', 'We help you explore if you\'re unsure, or execute if your target is set.', '#a78bfa'],
                  [Users, 'Belonging', 'Connection is one of the biggest predictors of finishing, we help you build it.', '#f472b6']].map(([Icon, t, d, hue]) => (
                  <div key={t} className="fp-tile p-4">
                    <span className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${hue}1f`, boxShadow: `0 0 18px -5px ${hue}` }}><Icon className="w-5 h-5" style={{ color: hue }} /></span>
                    <p className="text-sm font-bold text-white">{t}</p>
                    <p className="text-[12px] text-slate-300 leading-snug mt-1">{d}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[20px] p-5 sm:p-6 flex items-center justify-between gap-4 flex-wrap" style={GLOW_PANEL}>
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-cyan-500/15 flex items-center justify-center shrink-0 shadow-[0_0_22px_-5px_rgba(57,227,220,0.9)]"><Lock className="w-5 h-5 text-[#39E3DC]" /></span>
                <div>
                  <p className="text-sm font-bold text-white">Ready when you are.</p>
                  <p className="text-[12.5px] text-slate-300">Takes about two minutes. It's private, and you can update it anytime.</p>
                </div>
              </div>
              <button onClick={() => setPhase('questions')} data-testid="situation-start" className="btn-primary text-sm px-7 py-3 flex items-center gap-2">Let's begin <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {/* ═══ QUESTION CARD (glowing) ═══ */}
        {!done && phase === 'questions' && q && (
          <div className="relative overflow-hidden rounded-[20px] p-5 sm:p-6" style={GLOW_PANEL} data-testid="situation-card">
            <div aria-hidden className="pointer-events-none absolute -right-20 -top-24 h-56 w-56 rounded-full blur-3xl opacity-[0.18]" style={{ background: 'radial-gradient(circle,#39E3DC,transparent 62%)', zIndex: 0 }} />
            <div className="relative" style={{ zIndex: 1 }}>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-[#39E3DC] font-bold">Question {idx + 1} of {total}</span>
                <span className="text-slate-300">{pct}% complete</span>
              </div>
              <div className="h-2 rounded-full bg-white/8 overflow-hidden mb-5">
                <motion.div className="h-full rounded-full" animate={{ width: `${pct}%` }} style={{ background: 'linear-gradient(90deg,#39E3DC,#782FE1)', boxShadow: '0 0 12px rgba(57,227,220,0.7)' }} />
              </div>

              <h2 className="font-outfit text-2xl font-bold text-white">{q.title}</h2>
              {q.help && <p className="text-sm text-slate-300 mt-1.5">{q.help}</p>}

              <AnimatePresence mode="wait">
                <motion.div key={q.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-4">
                  {q.kind === 'tiles' ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="situation-options">
                      {q.options.map((o) => {
                        const on = (answers[q.id] || []).includes(o.value);
                        const Icon = ICONS[o.icon] || Star;
                        return (
                          <button key={o.value} onClick={() => toggleMulti(o.value)} data-testid={`opt-${q.id}-${o.value}`}
                            className={`group relative overflow-hidden rounded-xl border text-left p-4 transition-all duration-200 ${on ? 'border-cyan-400/70 bg-cyan-500/[0.12] shadow-[0_0_28px_-6px_rgba(34,211,238,0.8)]' : 'border-white/10 bg-white/[0.04] hover:border-cyan-400/40 hover:bg-white/[0.06] hover:shadow-[0_0_22px_-10px_rgba(57,227,220,0.7)]'}`}>
                            <span className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 transition-all ${on ? 'bg-cyan-500/20 shadow-[0_0_22px_-4px_rgba(57,227,220,0.9)]' : 'bg-white/[0.05] group-hover:bg-cyan-500/10'}`}>
                              <Icon className={`w-5 h-5 transition-colors ${on ? 'text-[#39E3DC]' : 'text-slate-300 group-hover:text-[#39E3DC]'}`} />
                            </span>
                            <p className="text-[12.5px] font-semibold text-white leading-snug pr-5">{o.label}</p>
                            <span className={`absolute top-3 right-3 w-5 h-5 rounded-md border flex items-center justify-center transition-all ${on ? 'border-cyan-300 bg-cyan-400/25' : 'border-white/20'}`}>{on && <Check className="w-3.5 h-3.5 text-[#39E3DC]" />}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="situation-options">
                      {q.options.map((o) => {
                        const on = q.kind === 'multi' ? (answers[q.id] || []).includes(o.value) : answers[q.id] === o.value;
                        return (
                          <button key={o.value} onClick={() => (q.kind === 'multi' ? toggleMulti(o.value) : setSingle(o.value))} data-testid={`opt-${q.id}-${o.value}`}
                            className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200 ${on ? 'border-cyan-400/70 bg-cyan-500/12 shadow-[0_0_20px_-8px_rgba(34,211,238,0.8)]' : 'border-white/10 bg-white/[0.04] hover:border-cyan-400/40'}`}>
                            <span className={`w-5 h-5 border flex items-center justify-center shrink-0 ${q.kind === 'multi' ? 'rounded-md' : 'rounded-full'} ${on ? 'border-cyan-400 bg-cyan-400/25' : 'border-white/25'}`}>{on && <Check className="w-3 h-3 text-[#39E3DC]" />}</span>
                            <span className="text-sm text-white">{o.label}</span>
                          </button>
                        );
                      })}
                      {q.allow_text && (
                        <input value={textVal} onChange={(e) => setTextVal(e.target.value)} placeholder="Something else? Type it here…" data-testid="situation-text"
                          className="w-full rounded-xl bg-[#030C1D]/70 border border-white/12 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/50 mt-1" />
                      )}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {q.why && (
                <div className="mt-5 rounded-xl border border-white/8 bg-white/[0.03] p-4 flex flex-col sm:flex-row gap-4 items-start justify-between" data-testid="situation-why">
                  <div className="flex gap-3">
                    <Info className="w-4 h-4 text-[#a78bfa] shrink-0 mt-0.5" />
                    <div><p className="text-[11px] uppercase tracking-wider text-slate-300 font-bold">Why we ask this</p><p className="text-[12.5px] text-slate-300 mt-0.5 leading-snug max-w-lg">{q.why}</p></div>
                  </div>
                  <p className="text-2xl text-[#39E3DC] shrink-0 hidden md:block text-right leading-tight" style={{ ...SCRIPT, textShadow: '0 0 26px rgba(57,227,220,0.4)' }}>"Your journey is unique.<br />That's your advantage."</p>
                </div>
              )}

              <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  {idx > 0 && <button onClick={back} data-testid="situation-back" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white px-3 py-2 rounded-lg border border-white/12"><ArrowLeft className="w-4 h-4" /> Back</button>}
                  <button onClick={() => { persist({}, false); toast.success('Saved, pick up anytime'); navigate('/app/college-dashboard'); }} data-testid="situation-save-later" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white px-3 py-2"><Bookmark className="w-4 h-4" /> Save &amp; continue later</button>
                </div>
                <button onClick={next} data-testid="situation-next" className="btn-primary text-sm px-6 py-2.5 flex items-center gap-2">
                  {idx < total - 1 ? <>Next question <ArrowRight className="w-4 h-4" /></> : <>See my readout <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ COMPLETION READOUT ═══ */}
        {done && (
          <div className="relative overflow-hidden rounded-[20px] p-6 sm:p-7" style={GLOW_PANEL} data-testid="situation-complete">
            <motion.div aria-hidden animate={{ opacity: [0.2, 0.4, 0.2] }} transition={{ duration: 6, repeat: Infinity }}
              className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle,#39E3DC,transparent 62%)' }} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-12 h-12 rounded-full bg-cyan-500/15 flex items-center justify-center shrink-0 shadow-[0_0_26px_-4px_rgba(57,227,220,0.95)]"><CheckCircle2 className="w-6 h-6 text-[#39E3DC]" /></span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#39E3DC] font-bold">Situation complete</p>
                  <h2 className="font-outfit text-2xl font-extrabold text-white">Here's what FuturePath understands about you</h2>
                </div>
              </div>

              {context && (
                <div className="space-y-5">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                    <span className="text-white font-bold text-lg">{context.name}</span>
                    {context.major && <span className="text-slate-300 text-sm">{context.major}</span>}
                    {context.class_year && <span className="text-slate-300 text-sm capitalize">{context.class_year}</span>}
                    {context.target_career && <span className="fp-pill"><Target className="w-3 h-3 text-[#39E3DC]" /> {context.target_career}</span>}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                      <p className="text-[11px] uppercase tracking-wider text-slate-300 font-bold mb-2">What we understand</p>
                      <div className="flex flex-wrap gap-2" data-testid="context-journey">
                        {(context.journey || []).map((c) => <span key={c} className="fp-pill">{c}</span>)}
                        {(context.journey || []).length === 0 && <span className="text-sm text-slate-300">No details yet.</span>}
                      </div>
                      <p className="text-[11px] uppercase tracking-wider text-slate-300 font-bold mt-4 mb-2">Your priorities</p>
                      <ul className="space-y-1" data-testid="context-priorities">
                        {(context.priorities || []).map((p) => <li key={p} className="text-[13px] text-slate-200 flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#39E3DC] shrink-0" /> {p}</li>)}
                      </ul>
                    </div>

                    <div className="rounded-xl border border-cyan-400/25 bg-cyan-500/[0.06] p-4 shadow-[0_0_34px_-16px_rgba(57,227,220,0.7)]">
                      <p className="text-[11px] uppercase tracking-wider text-[#39E3DC] font-bold mb-2 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> How your strategy changes</p>
                      <ul className="space-y-2" data-testid="context-impacts">
                        {(context.impacts || []).map((im) => <li key={im} className="text-[13px] text-slate-100 flex items-start gap-2"><ArrowRight className="w-3.5 h-3.5 text-[#39E3DC] mt-0.5 shrink-0" /> <span>{im}</span></li>)}
                      </ul>
                    </div>
                  </div>

                  {/* Agreement */}
                  <div className="rounded-xl border border-purple-400/30 bg-purple-500/[0.08] p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-5 h-5 text-[#a78bfa] shrink-0" />
                      <p className="text-sm text-slate-100">Use this to personalize your recommendations, plan and next moves across FuturePath?</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => { setDone(false); setIdx(0); }} data-testid="situation-update" className="text-sm text-slate-300 hover:text-white px-4 py-2.5 rounded-full border border-white/12">Update my answers</button>
                      <button onClick={agree} data-testid="situation-agree" className="btn-primary text-sm px-6 py-2.5 whitespace-nowrap">Yes, personalize my journey</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
